import { useState } from 'react'
import {
  AlertTriangle,
  Check,
  Copy,
  Loader2,
  MessageSquare,
  RefreshCw,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import { useReissueUploadLink } from '@/admin/pages/claims/use-send-packet'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { toastApiError } from '@/shared/lib/api-error'

interface ReissueResult {
  revoked: number
  url: string
  recipientEmail: string
  email: { status: string; reason?: string }
  sms: { status: string; reason?: string } | null
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

/**
 * Revoke & reissue the claim's evidence upload link, then deliver the fresh one.
 *
 * Email is always sent (a reissued link is useless until it reaches someone);
 * SMS is an optional add-on, shown only when the tenant has SMS enabled. The
 * recipient defaults to the customer on file but is editable - the point of a
 * reissue is that the link reached the wrong person. The new link is also shown
 * for manual sharing.
 */
export function ReissueUploadLinkDialog({
  open,
  onOpenChange,
  claimUuid,
  defaultEmail,
  defaultPhone,
  recipientName,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  claimUuid: string
  defaultEmail?: string
  defaultPhone?: string
  recipientName?: string
}) {
  const { t } = useTranslation()
  const smsAvailable = useFeatureFlag(FLAGS.SMS)
  const reissue = useReissueUploadLink(claimUuid)
  const [email, setEmail] = useState(defaultEmail ?? '')
  const [phone, setPhone] = useState(defaultPhone ?? '')
  const [alsoSms, setAlsoSms] = useState(false)
  const [result, setResult] = useState<ReissueResult | null>(null)
  const [copied, setCopied] = useState(false)

  // Reset to the customer-on-file defaults on close (not to empty) so a reopen
  // is prefilled again. Done at the event-handler level, not a useEffect.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setEmail(defaultEmail ?? '')
      setPhone(defaultPhone ?? '')
      setAlsoSms(false)
      setResult(null)
      setCopied(false)
      reissue.reset()
    }
    onOpenChange(next)
  }

  const emailValid = EMAIL_RE.test(email.trim())
  const canSubmit =
    !reissue.isPending &&
    emailValid &&
    (!alsoSms || !!smsAvailable) &&
    (!alsoSms || phone.trim().length > 0)

  const handleReissue = async () => {
    try {
      const res = await reissue.mutateAsync({
        recipient_email: email.trim(),
        send_sms: smsAvailable && alsoSms,
        sms_contact: phone.trim(),
      })
      if (res.email.status === 'sent') {
        // Delivered - no need to hand the admin the link to copy. Confirm and
        // close, mirroring the Send-packet dialog. The old link is dead; the
        // customer now has the new one.
        let msg = t(
          'claims.reissueDialog.toastEmailed',
          'New link emailed to {{email}}.',
          { email: res.recipient_email },
        )
        if (res.sms) {
          msg +=
            ' ' +
            (res.sms.status === 'sent'
              ? t('claims.reissueDialog.toastAlsoTexted', 'Also texted.')
              : t(
                  'claims.reissueDialog.toastSmsFailed',
                  'SMS not sent ({{reason}}).',
                  { reason: res.sms.reason || res.sms.status },
                ))
        }
        toast.success(msg)
        handleOpenChange(false)
        return
      }
      // Email failed: a fresh link exists but wasn't emailed. The old one is
      // retired only when the link reached the customer another way (SMS).
      setResult({
        revoked: res.revoked,
        url: res.upload_session_url,
        recipientEmail: res.recipient_email,
        email: res.email,
        sms: res.sms,
      })
    } catch (err) {
      toastApiError(
        err,
        t('claims.reissueDialog.error', "Couldn't reissue the upload link."),
      )
    }
  }

  const handleCopy = async () => {
    if (!result) return
    try {
      await navigator.clipboard.writeText(result.url)
      setCopied(true)
      toast.success(t('claims.reissueDialog.linkCopied', 'Link copied.'))
    } catch {
      toast.error(
        t(
          'claims.reissueDialog.copyFailed',
          'Copy failed - select and copy the link manually.',
        ),
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>
            {t('claims.reissueDialog.title', 'Revoke & reissue upload link')}
          </DialogTitle>
          <DialogDescription>
            {result
              ? result.revoked > 0
                ? t(
                    'claims.reissueDialog.descriptionError',
                    "The old link no longer works and a fresh one is active, but the email couldn't be sent. Share the new link manually below.",
                  )
                : t(
                    'claims.reissueDialog.descriptionNotRevoked',
                    "The email couldn't be sent, so the old link still works. A fresh link is active - share it manually below, then reissue again to retire the old one.",
                  )
              : t(
                  'claims.reissueDialog.description',
                  'This revokes the current evidence upload link, mints a fresh one, and sends it. Use it when the link reached the wrong person.',
                )}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 py-2">
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div className="space-y-0.5">
                <div>
                  {t(
                    'claims.reissueDialog.emailFailed',
                    'Email could not be sent ({{reason}}) to {{email}}.',
                    {
                      reason: result.email.reason ?? 'error',
                      email: result.recipientEmail,
                    },
                  )}
                </div>
                {result.sms && result.sms.status === 'sent' && (
                  <div>
                    {t(
                      'claims.reissueDialog.smsTexted',
                      'The link was texted to the customer.',
                    )}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="reissued-url" className="text-xs">
                {t('claims.reissueDialog.newLinkLabel', 'New upload link')}
              </Label>
              <div className="mt-1 flex items-center gap-2">
                <Input id="reissued-url" readOnly value={result.url} />
                <Button variant="outline" size="icon" onClick={handleCopy}>
                  {copied ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {t(
                  'claims.reissueDialog.warning',
                  'The current link will stop working immediately. Anyone who already received it will need the new one.',
                )}
              </span>
            </div>

            <div>
              <Label htmlFor="reissue-email" className="text-xs">
                {t('claims.reissueDialog.emailLabel', 'Email the link to')}
              </Label>
              <Input
                id="reissue-email"
                type="email"
                placeholder={t(
                  'claims.reissueDialog.emailPlaceholder',
                  'customer@example.com',
                )}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
              />
              {recipientName && (
                <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                  {t(
                    'claims.reissueDialog.customerOnFile',
                    'Customer on file: {{name}}',
                    { name: recipientName },
                  )}
                </p>
              )}
            </div>

            {smsAvailable && (
              <div className="space-y-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setAlsoSms((v) => !v)}
                  className={`h-auto w-full justify-start rounded-md border p-3 text-left font-normal transition-all ${
                    alsoSms
                      ? 'border-[var(--ink)] bg-[var(--bg-light)] hover:bg-[var(--bg-light)]'
                      : 'border-[var(--rule)] hover:border-[var(--ink-3)] hover:bg-transparent'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        alsoSms
                          ? 'border-[var(--ink)] bg-[var(--ink)]'
                          : 'border-[var(--rule-strong)]'
                      }`}
                    >
                      {alsoSms && (
                        <svg
                          viewBox="0 0 14 14"
                          className="h-2.5 w-2.5 text-[var(--bg)]"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <path d="M2 7l3 3 7-7" />
                        </svg>
                      )}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center gap-1.5 text-sm font-medium text-[var(--ink)]">
                        <MessageSquare className="h-3.5 w-3.5" />
                        {t(
                          'claims.reissueDialog.alsoText',
                          'Also text the customer',
                        )}
                      </div>
                      <div className="mt-0.5 whitespace-normal text-xs text-[var(--ink-3)]">
                        {t(
                          'claims.reissueDialog.alsoTextDescription',
                          'Sends the link by SMS too (needs a mobile number and SMS opt-in).',
                        )}
                      </div>
                    </div>
                  </div>
                </Button>
                {alsoSms && (
                  <div>
                    <Label htmlFor="reissue-phone" className="text-xs">
                      {t('claims.reissueDialog.phoneLabel', 'Mobile number')}
                    </Label>
                    <Input
                      id="reissue-phone"
                      type="tel"
                      placeholder={t(
                        'claims.reissueDialog.phonePlaceholder',
                        '+1 555 123 4567',
                      )}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => handleOpenChange(false)}>
              {t('claims.reissueDialog.done', 'Done')}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={reissue.isPending}
              >
                {t('claims.reissueDialog.cancel', 'Cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleReissue}
                disabled={!canSubmit}
                className="gap-1.5"
              >
                {reissue.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
                {reissue.isPending
                  ? t('claims.reissueDialog.buttonSending', 'Sending…')
                  : t(
                      'claims.reissueDialog.buttonReissue',
                      'Revoke, reissue & send',
                    )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ReissueUploadLinkDialog
